'use strict';

const { SKI } = require('../src/index');
const { Store } = require('./store');
const { EvalBox } = require('./eval-box');
const { append } = require('./html-util');
const { Quest } = SKI.quest;

const days = 24 * 60 * 60 * 1000;

/**
 * @cssClass ski-quest-box - container for an individual quest, with title, description, input, and result display
 * @cssSelector .ski-quest-box h3 - quest title
 * @cssClass ski-quest-chapter - container for a chapter, with title and intro
 * @cssSelector .ski-quest-chapter h2 - chapter title, click to show/hide content
 * @cssSelector .ski-quest-chapter code - how to display code snippets in quest and chapter intros
 * @cssClass ski-quest-chapter-content - container for quests in a chapter, can be hidden by clicking chapter title
 * @cssClass ski-quest-chapter-intro - chapter introduction text
 * @cssClass ski-quest-comment - comments, make them fainter or something
 * @cssClass ski-quest-progressbar - a progress bar in the chapter list, showing how much of the chapter is solved
 *           Set background [color], and don't touch margin/padding which are adjusted by quest code
 * @cssClass ski-quest-control - for controls in the result display, like "show steps"
 * @cssClass ski-quest-display - results of a quest solution attempt
 * @cssClass ski-quest-error
 * @cssClass ski-quest-float-right
 * @cssClass ski-quest-hint - a self-revealing spoiler
 * @cssClass ski-quest-nav - container for chapter navigation.
 * @cssClass ski-quest-nav-item - individual chapter links inside ski-quest-nav.
 * @cssClass ski-quest-intro - introduction text in individual quests
 * @cssClass ski-quest-inventory - element containing all unlocked terms
 * @cssClass ski-quest-label - label for an input field
 * @cssClass ski-quest-input - container for input elements
 * @cssSelector .ski-quest-input input[type="text"] - input fields for quest solutions
 * @cssSelector .ski-quest-input button - button to submit quest solution
 * @cssClass ski-quest-term-def
 * @cssClass ski-quest-term-name
 */

class QuestPage {
  /**
   * @desc  A collection of combinatory logic quests organized into chapters
   *        and attached to specific elements for display.
   *
   *        Quest progress is saved in the localStorage under a given prefix.
   *
   *        The engine is also saved and loaded from the store,
   *        so that unlocked terms persist across sessions and can be used in later quests.
   *
   * @param {{
   *   index: string, // URL to fetch quest list from
   *   baseUrl?: string, // root URL to fetch quest data from, default 'data/quests/'
   *   storePrefix?: string, // prefix for localStorage keys, required unless 'store' object itself is provided
   *   // page elements to attach to:
   *   indexBox: HTMLElement, // element to attach chapter list to
   *   contentBox: HTMLElement, // element to attach chapter content to
   *   inventoryBox: HTMLElement, // element to attach inventory to
   *   // extra stuff
   *   linkedTo?: string, // id of element to scroll into view after loading
   *   store: Store, // TODO move out into callbacks, also make async
   *   engine?: SKI, // defautl = new SKI()
   *   onLoad?: function, // callback for when quests are loaded, gets list of QuestChapter objects as argument
   *   onSolved?: function, // callback for when a quest is solved
   *   onFailed?: function, // callback for when a quest is attempted but not solved
   *   onTermUnlock?: function, // callback for when a quest is solved and unlocks something in the engine
   *   chapterList?: QuestChapter[], // optional write-only list for observability only
   *   newCutOff?: Date, // optional date to mark quests as "new" if created after this date
   * }} options
   */
  constructor (options) {
    this.view = {};
    this.root = options.baseUrl ?? '.';

    if (!options.store && !options.storePrefix)
      throw new Error('No storePrefix provided');
    this.store = options.store ?? new Store(options.storePrefix);
    this.defaultEngine = options.engine;

    if (options.inventoryBox)
      this.view.inventory = options.inventoryBox;

    this.view.content = options.contentBox;
    if (options.indexBox)
      this.view.index = append(options.indexBox, 'div', { class: ['ski-quest-nav'] });

    this._onSolved = options.onSolved;
    this._onFailed = options.onFailed;
    this._onTermUnlock = options.onTermUnlock;
    this._onTermReplace = options.onTermReplace;

    this.chapters = [];
  }

  /**
   * @desc Load quest list from given index URL, then load and draw chapters.
   *
   * Not part of the constructor since it involves async operations
   *
   * @param {string} index URL to fetch the quest list from
   * @param {string} [linkedTo] id of quest to scroll into view after loading, if matches
   * @param {function} onLoad callback for when quests are loaded, gets self as argument
   */
  loadFromIndex (index, linkedTo, onLoad) {
    // TODO update this.root based on index URL
    fetch(this.mkLink(index))
      .then(resp => resp.json())
      .then(data => {
        if (!Array.isArray(data.chapters))
          throw new Error('Invalid quest index in ' + index);
        if (data.new_age)
          this.newCutOff = new Date(new Date() - data.new_age * days);

        // TODO this is a horrible effectful elseif with faulty logic, rewrite when have time
        const oldEngine = this.store.load('engine');
        if (oldEngine)
          this.engine = new SKI(oldEngine);
        else if (this.defaultEngine)
          this.engine = this.defaultEngine;
        else {
          this.engine = new SKI({ annotate: true, allow: 'I-I' });
          this.engine.bulkAdd(data.inventory ?? ['S', 'K', 'I']);
        }

        this.showKnown();
        return this.loadChapters(data.chapters);
      })
      .then(self => {
        if (onLoad)
          onLoad(self);
        if (linkedTo) {
          const target = document.getElementById(linkedTo);
          if (target)
            target.scrollIntoView();
        }
      });
  }

  /**
   * @desc Load and draw chapters from a given list of chapter specs.
   * @param list
   * @returns {Promise<QuestPage>}
   */
  async loadChapters (list) {
    let chapterId = 0;
    this.chapters = [];
    const joint = [];
    for (const item of list) {
      // TODO if item is an object, skip fetch and use it directly, returning `resolve`
      const chapter = new QuestChapter({
        number:        ++chapterId,
        link:          this.mkLink(item),
        engine:        this.engine,
        store:         this.store,
        onTermUnlock:  x => this.onTermUnlock(x),
        onTermReplace: x => this.onTermReplace(x),
        onSolved:      x => this._onSolved(x),
        onFailed:      x => this._onFailed(x),
        newCutOff:     this.newCutOff,
      });
      this.chapters.push(chapter);
      chapter.attach(this.view.content, { placeholder: 'loading chapter' + chapter.number + '...' });
      if (this.view.index)
        chapter.addLink(this.view.index);
      joint.push(chapter.fetch().then(chapter => {
        chapter.draw();
      }));
    }
    return Promise.all(joint).then(() => this);
  }

  mkLink (str) {
    return (str.match(/^\w+:\/\//) || str.match(/^[/.]/)) ? str : this.root + '/' + str;
  }

  onTermUnlock (term) {
    this.engine.maybeAdd(term.name, term.impl);
    if (this.store)
      this.store.save('engine', this.engine);
    this.showKnown();
    if (this._onTermUnlock)
      this._onTermUnlock(term);
  }

  /**
   * @desc Force-replace an already unlocked term in the engine with a new implementation,
   *       unless the currently known term with the same name is a Primitive (built-in) combinator,
   *       which must never be overridden.
   * @param term
   */
  onTermReplace (term) {
    const known = this.engine.getTerms()[term.name];
    if (known instanceof SKI.classes.Primitive)
      return;
    this.engine.add(term.name, term.impl);
    if (this.store)
      this.store.save('engine', this.engine);
    this.showKnown();
    if (this._onTermUnlock)
      this._onTermUnlock(term);
  }

  showKnown () {
    if (!this.view.inventory)
      return;
    this.view.inventory.innerHTML = '';
    const ul = append(this.view.inventory, 'ul', { class: ['ski-quest-inventory'] });
    const terms = this.engine.getTerms();
    for (const [name, term] of Object.keys(terms).sort().map(x => [x, terms[x]])) {
      if (name === '+')
        continue; // the presence of '+' depends on whether the quests allow numbers anyway
      const li = append(ul, 'li', {}, e => {
        e.dataset.skiTerm = name;
      });
      append(li, 'span', { content: name, class: ['ski-quest-term-name'] });
      append(li, 'span', { content: ': ' });
      append(li, 'span', { content: showTerm(term), class: ['ski-quest-term-def'] });
    }
  }

  demolish () {
    for (const key of this.store.scan())
      this.store.delete(key);

    // TODO reset engine, chapters, and quest boxes
  }
}

class QuestBox {
  /**
   * @desc Create a quest box with given spec and options
   * @param {QuestSpec} spec
   * @param {{
   *   engine?: SKI,
   *   chapter?: QuestChapter,
   *   number?: number,
   *   store?: Store,
   *   newCutOff?: Date,
   * }}options
   */
  constructor (spec, options) {
    const engine = options.engine ?? (options.chapter?.engine);
    if (!engine)
      throw new Error('QuestBox requires an engine: SKI in either options or chapter');
    const store = options.store ?? options.chapter?.store;
    if (!store)
      throw new Error('QuestBox requires a store: Store in either options or chapter');
    this.impl = new Quest({ ...spec, engine });
    this.name = this.impl.id ? 'quest-' + this.impl.id : '';
    this.isNew = options.newCutOff && this.impl.created && this.impl.created > options.newCutOff;
    this.chapter = options.chapter;
    if (this.chapter && options.number)
      this.number = this.chapter.number + '.' + options.number;
    this.store = store;
    this.engine = engine;
    this.view = {};
    this.input = [];
  }

  load () {
    const data = this.store.load(this.name) ?? {};
    this.status = {
      // TODO remove `solved` in favor of just checking if `solution` is there
      solved:   !!data.solution || !!data.solved,
      solution: data.solution,
      steps:    data.steps ?? 0,
      attempts: data.attempts ?? 0,
      weight:   data.weight ?? 0,
      total:    data.total ?? 0,
    };
    // TODO Wrong! will cause a salute on loading
    if (this.status.solved)
      this.onSolved();
    return this;
  }

  save () {
    this.store.save(this.name, this.status);
    return this;
  }

  update (result, input) {
    if (this.status.solved) {
      // already solved: offer to replace the saved solution instead of touching stats right away
      this.pendingReplacement = result.pass ? { result, input } : null;
      this.showReplace();
      return;
    }
    this.status.attempts++;
    this.status.total += result.steps;
    this.status.steps = result.steps;
    this.status.weight = result.weight;
    if (result.pass) {
      this.status.solved = true;
      this.status.solution = input;
      this.onSolved(result);
    }
    this.save();
    this.showStatus();
    this.showReveal();
  }

  onSolved (result) {
    if (this.impl.meta.unlock && result) {
      const term = new SKI.classes.Alias(this.impl.meta.unlock, result.expr.expand());
      this.chapter?.onTermUnlock(term);
    }
    if (this.chapter)
      this.chapter.addSolved(this.impl.id);
  }

  /**
   * @desc Replace an already saved solution with the latest successful attempt.
   *       Only available right after a successful re-attempt of an already solved quest.
   *       Also replaces the corresponding term in the engine, unless it is a Primitive term
   *       (i.e. one of the built-in combinators, which must never be overridden).
   */
  replaceSolution () {
    if (!this.pendingReplacement)
      return;
    const { result, input } = this.pendingReplacement;

    this.status.attempts++;
    this.status.solution = input;
    this.status.steps = result.steps;
    this.status.weight = result.weight;
    this.save();

    this.pendingReplacement = null;
    this.showReplace();
    this.showStatus();

    if (this.impl.meta.unlock) {
      const term = new SKI.classes.Alias(this.impl.meta.unlock, result.expr.expand());
      this.chapter?.onTermReplace(term);
    }
  }

  check () {
    if (this.view.display)
      this.view.display.innerHTML = 'running...';
    const got = this.input.map(x => x.value);
    const result = this.impl.check(...got);
    this.showResult(result);
    this.update(result, got);
  }

  draw (element) {
    this.view.frame = append(element, 'div', { class: ['ski-quest-box'] });
    this.view.frame.id = this.name;

    const title = append(this.view.frame, 'h3');
    const body = append(this.view.frame, 'div');
    const expand = append(title, 'a', { content: this.number ? '#' + this.number + '' : 'Quest' });
    expand.href = '#' + this.name;
    expand.onclick = () => showhide(body, true);

    append(title, 'span', { content: ' ' + this.impl.name });
    const allowed = this.impl.allowed();
    if (allowed)
      append(title, 'span', { content: ' [' + allowed + ']' });
    this.view.stat = append(title, 'span', { class: ['ski-quest-float-right'] });

    const descr = append(body, 'div');
    if (this.isNew)
      append(descr, 'div', { content: 'New!', class: ['ski-quest-new'] });
    append(descr, 'div', { content: cat(this.impl.intro), class: ['ski-quest-intro'] });
    if (this.impl.meta.hint)
      hint(descr, ' Hint:...', ' Hint: ' + this.impl.meta.hint);

    this.view.display = append(body, 'div', { class: ['ski-quest-display'], content: '.....' });

    this.view.solution = append(body, 'div', { class: ['ski-quest-input'] });

    this.drawInput(this.view.solution);

    this.showStatus();
  }

  drawInput (element) {
    const spec = this.impl.input;
    const multi = spec.length !== 1;

    for (const item of spec) {
      if (multi) {
        const label = append(element, 'div', { class: ['ski-quest-label'] });
        append(label, 'b', { content: item.name });
        if (item.note)
          append(label, 'span', { content: ' // ' + item.note, class: ['ski-quest-comment'] });
      }
      const input = append(element, 'input');
      input.type = 'text';
      input.onkeydown = e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.check();
        }
      };
      this.input.push(input);
      append(element, 'br');
    }

    append(element, 'button', { content: 'solve!' }, btn => {
      btn.onclick = () => this.check();
    });
    this.view.reveal = append(element, 'button', { content: 'reveal', hidden: !this.status.solution }, btn => {
      btn.onclick = () => {
        for (let i = 0; i < this.status.solution.length; i++)
          this.input[i].value = this.status.solution[i];
      }
    });
    this.view.replace = append(element, 'button', { content: 'replace', hidden: !this.pendingReplacement }, btn => {
      btn.onclick = () => this.replaceSolution();
    });
  }

  showReveal () {
    if (this.view.reveal)
      this.view.reveal.hidden = !this.status.solution;
  }

  showReplace () {
    if (this.view.replace)
      this.view.replace.hidden = !this.pendingReplacement;
  }

  showStatus () {
    if (!this.view.stat)
      return;
    if (this.status.attempts) {
      const tries = 'in ' + this.status.attempts + (this.status.attempts === 1 ? ' try' : ' tries');
      const steps = this.status.solved
        ? '&check; ' + this.status.steps + ' steps/' + this.status.weight + ' terms '
        : this.status.total + ' total steps ';
      this.view.stat.innerHTML = steps + ' ' + tries;
    }
  }

  /**
   *
   * @param {QuestResult} result
   */
  showResult (result) {
    if (!this.view.display)
      return;
    this.view.display.innerHTML = '';
    const echo = append(this.view.display, 'div');
    append(echo, 'span', { content: 'Your solution: ' + expand(result.expr) + ' ' });

    if (result.exception)
      append(this.view.display, 'div', { class: ['ski-quest-error'], content: 'Execution failed: ' + result.exception });

    for (const item of result.details) {
      const line = append(this.view.display, 'div', { class: item.pass ? ['ski-quest-success'] : ['ski-quest-error'] });
      append(line, 'span', { content: item.pass ? '&check; ' : '&cross; ' });
      append(line, 'span', { content: `${item.start} &rarr; ${item.found} ` });
      const showSteps = append(line, 'a', { content: `in ${item.steps} steps`, class: ['ski-quest-control'] });
      append(line, 'span', { content: ' ' });
      const hideSteps = append(line, 'a', { content: ' (hide)', class: ['ski-quest-control'], hidden: true });

      if (!item.pass) {
        append(line, 'br');
        if (item.expected !== undefined) {
          append(line, 'span', { content: '&nbsp;&nbsp;' + 'expected: ' + item.expected });
          append(line, 'br');
        }
        if (item.reason) {
          append(line, 'span', { content: '&nbsp;&nbsp;' + item.reason });
          append(line, 'br');
        }
      }
      // replay specific test case via EvalBox
      const termDiv = append(line, 'div', {});
      showSteps.onclick = () => {
        termDiv.innerHTML = '';
        hideSteps.hidden = false;
        const box = new EvalBox({ parent: termDiv, engine: this.engine, height: Infinity, max: item.steps + 2, headless: true });
        box.start(item.start);
      };
      hideSteps.onclick = () => {
        termDiv.innerHTML = '';
        hideSteps.hidden = true;
      };
    }
  }
}

class QuestChapter {
  /**
   * @desc A collection of quests, typically related,
   *       with a title and intro text. Optionally numbered, too.
   * @param {{
   *   name?: string,
   *   intro?: string|string[],
   *   link: string, // URL to fetch quest list from
   *   number?: number,
   *   engine: SKI,
   *   store: Store,
   *   onTermUnlock?: function, // callback for when a quest is solved and unlocks something in the engine
   *   newCutOff?: Date, // optional date to mark quests as "new" if created after this date
   * }}options
   */
  constructor (options) {
    this.options = options;
    this.quests = [];
    this.solved = new Set();
    this.view = {};
    this.number = options.number ?? 0;
    this.engine = options.engine;
    this.store = options.store;
    this.newCutOff = options.newCutOff;
    this.onTermUnlock = options.onTermUnlock ?? (() => {});
    this.onTermReplace = options.onTermReplace ?? (() => {});
    this.updateMeta();
  }

  updateMeta (meta = {}) {
    this.options = { ...this.options, ...meta };
    this.id = 'chapter-' + (meta.id ?? this.number);
    if (this.view.frame)
      this.view.frame.id = this.id;
    if (this.view.link)
      this.view.link.href = '#' + this.id;
    if (this.options.name && this.view.linkText)
      this.view.linkText.innerHTML = 'Chapter ' + this.number + ': ' + this.options.name;
  }

  fetch () {
    return fetch(this.options.link)
      .then( resp => resp.json() )
      .then(data => {
        if (Array.isArray(data))
          data = { content: data };
        if (!Array.isArray(data.content))
          throw new Error('Invalid quest list in ' + this.options.link);

        this.updateMeta(data);

        let k = 0;
        for (const item of data.content) {
          const box = new QuestBox(item, {
            chapter:   this,
            number:    ++k,
            newCutOff: this.newCutOff,
          }).load();
          this.quests.push(box);
          if (box.status.solved)
            this.solved.add(box.impl.id);
        }

        this.showStatus();

        return this;
      });
  }

  addSolved (questId) {
    if (this.solved.has(questId))
      return;
    this.solved.add(questId);
    this.showStatus();
  }

  getProgress () {
    return {
      total:      this.quests.length,
      solved:     this.solved.size,
      complete:   this.solved.size === this.quests.length,
      percentage: Math.round(this.solved.size / this.quests.length * 100),
    }
  }

  attach (element, options) {
    this.view.frame = append(element, 'div', { class: ['ski-quest-chapter'] });
    this.view.frame.id = this.id;

    if (options.placeholder)
      this.view.placeholder = append(this.view.frame, 'div', { content: options.placeholder });
    return this;
  }

  draw () {
    this.visible = true;
    this.view.placeholder?.remove();
    const title = append(this.view.frame, 'h2');
    const body = append(this.view.frame, 'div');
    append(title, 'span', { content: 'Chapter ' + this.number + ': ' + this.options.name });
    this.view.stat = append(title, 'span', { class: ['ski-quest-float-right'] });
    title.onclick = () => { showhide(body, this.visible = !this.visible) };

    this.view.intro = append(body, 'div', { content: cat(this.options.intro), class: ['ski-quest-note', 'ski-quest-chapter-intro'] });
    this.view.content = append(body, 'div', { class: ['ski-quest-chapter-content'] });

    for (const quest of this.quests) {
      quest.load();
      quest.draw(this.view.content);
    }

    this.showStatus();
  }

  showStatus () {
    if (!this.view.stat)
      return;
    const progress = this.getProgress();
    this.view.stat.innerHTML = 'Progress: ' + progress.solved + '/' + progress.total + ' (' + progress.percentage + '%)';
    if (progress.complete)
      this.view.stat.classList.add('success');
    if (this.view.progressbar) {
      this.view.progressbar.style.paddingRight = progress.percentage + '%';
      this.view.progressbar.style.marginRight = -progress.percentage + '%';
    }
  }

  addLink (element) {
    // create an extra wrapper to allow messing with link's layout w/o interfering with the progress bar inside it
    const wrapper = append(element, 'div', { class: ['ski-quest-nav-item'] });
    const link = append(wrapper, 'a', {}, e => {
      e.style.display = 'flex'; // force flex for progress bar to work, don't rely on css
    });
    link.href = '#' + this.id;
    this.view.link = link;
    this.view.progressbar = append(link, 'span', { class: ['ski-quest-progressbar'] });
    this.view.linkText = append(link, 'span', { content: 'Chapter ' + this.number + '...' });
  }

  // TODO hide solved chapters
}

/**
 * @desc Create a self-revealing spoiler
 * @param element
 * @param shown
 * @param hidden
 */

function hint (element, shown, hidden) {
  const container = append(element, 'span', {});
  const clickme = append(container, 'span', { content: shown, class: ['ski-quest-hint'] });
  clickme.onclick = () => {
    clickme.remove();
    append(container, 'span', { content: hidden });
  };
}

/**
 * @desc Coerce array of strings to string
 * @param {string[]|string|number} input
 * @return {string}
 */
function cat (input) {
  if (Array.isArray(input))
    return input.join(' ');
  else
    return '' + input;
}

function expand (expr) {
  return expr instanceof SKI.classes.Expr
    ? (
      expr instanceof SKI.classes.Alias
        ? expr.name + ' = ' + expr.expand()
        : '' + expr.expand()
    )
    : '' + expr;
}

function showhide (element, show) {
  if (show === undefined)
    show = element.hidden;
  element.hidden = !show;
}

function showTerm (term) {
  return term.note ?? (term.impl ?? term).format({ html: true, lambda: ['', ' &mapsto; ', ''] });
}

module.exports = { QuestPage, QuestChapter, QuestBox };
